import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { ProtocolItem } from "../../components/protocol/ProtocolItem";
import { Add } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import { AddProtocolDialog } from "../../components/protocol/AddProtocolDialog";
import { useSnackbar } from "notistack";

const ProtocolPage = () => {
  const { token } = useAuthContext();
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [protocols, setProtocols] = useState([]); // State to store exercises fetched from backend
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [filteredProtocols, setFilteredProtocols] = useState([]); // State for filtered exercises
  const { darkMode } = useContext(ThemeContext);
  const [openAddDialog, setOpenAddDialog] = useState(false); // New state for confirmation dialog
  const { enqueueSnackbar } = useSnackbar();

  const updateProtocol = (updatedProtocol) => {
    setProtocols((prevProtocols) => {
      let updatedProtocols;

      if (updatedProtocol.deleted) {
        // Remove the deleted exercise from the state
        updatedProtocols = prevProtocols.filter(
          (protocol) => protocol.id !== updatedProtocol.id
        );
      } else if (
        !prevProtocols.some((protocol) => protocol.id === updatedProtocol.id)
      ) {
        // Add the new exercise if it doesn't already exist
        updatedProtocols = [...prevProtocols, updatedProtocol];
      } else {
        // Update the existing exercise
        updatedProtocols = prevProtocols.map((protocol) =>
          protocol.id === updatedProtocol.id ? updatedProtocol : protocol
        );
      }

      // Sort exercises alphabetically by name
      return updatedProtocols.sort((a, b) =>
        a.protocolname.localeCompare(b.protocolname, undefined, {
          sensitivity: "base",
        })
      );
    });
  };

  useEffect(() => {
    // Fetch exercises from the backend
    if (!token) return;
    const fetchProtocols = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/ebprotocol`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setProtocols(response.data);
        setFilteredProtocols(response.data); // Initialize filtered exercises
      } catch (err) {
        console.error("Error fetching protocols:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProtocols();
  }, [token]);

  useEffect(() => {
    // Filter exercises based on the search query
    setFilteredProtocols(
      protocols.filter((protocol) =>
        protocol.protocolname.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, protocols]);

  const handleAddProtocol = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/ebprotocol`,
        {
          protocolname: data.protocolname,
          conditions: data.conditions,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await axios.post(
        `${process.env.REACT_APP_API_URL}/ebprotexercise/bulkcreate`,
        {
          protocolid: response.data.id,
          exercises: data.exercises,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      updateProtocol(response.data); // Update the parent state
      enqueueSnackbar("Successfully Copied!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to add phase!", { variant: "error" });
    }
  };

  const handleAddClicked = () => {
    setOpenAddDialog(true);
  };

  const handleCancelAdd = () => {
    setOpenAddDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          pt: "60px",
          pl: 5,
          pr: 10,
        }}
      >
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
        {!loading && (
          <>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "28px",
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                fontWeight: 600,
                textAlign: "left",
                mb: 5,
              }}
            >
              EB Exercise Protocols
            </Typography>
            <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
              <TextField
                variant="filled"
                placeholder="Search protocol"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <SearchIcon sx={{ mt: -2, ml: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                startIcon={<Add />}
                variant="contained"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 500,
                  px: 3,
                  py: 2,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleAddClicked}
              >
                Add EB Protocol
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={4}
              rowGap={4}
            >
              {filteredProtocols.map((protocol, index) => (
                <ProtocolItem
                  key={index}
                  {...protocol}
                  onProtocolUpdate={updateProtocol}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
      <AddProtocolDialog
        open={openAddDialog}
        onClose={handleCancelAdd}
        onAdd={handleAddProtocol}
      />
    </>
  );
};

export default ProtocolPage;
